import { useCallback, useEffect, useState } from "react";

/**
 * Single source of truth for "can this browser install us, and has it?".
 *
 * Every install surface (the landing CTA, the dashboard card, the floating
 * banner) reads from here rather than wiring up its own listeners — which is
 * what previously let two of them disagree about whether an install was
 * even possible.
 *
 * ── Why the event is captured outside React ──────────────────────────────
 * `beforeinstallprompt` fires **once**, very early — typically as soon as the
 * manifest and service worker are evaluated, which is before React has
 * mounted anything. A listener attached inside a `useEffect` therefore misses
 * it on most page loads, and misses it *always* on a route the user navigates
 * to later. The browser never re-fires it for the same page.
 *
 * So the event is captured twice over, both outside the component tree:
 *   1. An inline snippet in index.html stashes it on
 *      `window.__qfDeferredInstallPrompt` before the bundle even downloads.
 *   2. The module-level listener below, attached at import time, catches it
 *      if the bundle happened to evaluate first.
 * Whichever wins, the deferred event ends up in the same module-level slot and
 * components subscribe to it. This is the whole reason the hook keeps state
 * outside React instead of per-component.
 */

// Shared with the pre-React snippet in index.html — keep both in sync.
const GLOBAL_SLOT = "__qfDeferredInstallPrompt";
const GLOBAL_EVENT = "qf:installprompt";

// sessionStorage, not localStorage: dismissing the banner should only
// silence it for the rest of THIS browser session — it must come back the
// next time the browser itself is closed and reopened, for every visitor,
// new or returning, each time they arrive at Landing/Dashboard.
const DISMISS_KEY = "qf_pwa_dismissed_v1";

/** The captured event, or null. Lives at module scope: see the note above. */
let deferredPrompt =
  (typeof window !== "undefined" && window[GLOBAL_SLOT]) || null;

const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => fn());

if (typeof window !== "undefined") {
  // Catches the event if this module evaluated before it fired. The inline
  // snippet in index.html covers the (more common) opposite ordering.
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  // Picks up a capture made by the inline snippet.
  window.addEventListener(GLOBAL_EVENT, () => {
    deferredPrompt = window[GLOBAL_SLOT] || deferredPrompt;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    // A consumed/expired prompt can't be reused — drop it so `canInstall`
    // reports honestly.
    deferredPrompt = null;
    window[GLOBAL_SLOT] = null;
    // Installing supersedes any earlier dismissal: `isInstalled` hides the
    // surfaces from here on, and if the app is later uninstalled the prompts
    // should be allowed back rather than staying permanently dismissed.
    safeRemoveDismissed();
    notify();
  });
}

// sessionStorage throws in Safari private mode and when storage is full, and
// a blocked read must never take the install UI down with it.
function safeReadDismissed() {
  try {
    return Boolean(window.sessionStorage.getItem(DISMISS_KEY));
  } catch {
    return false;
  }
}
function safeWriteDismissed(value) {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, value);
  } catch {
    /* non-fatal — the surface still hides for this session via React state */
  }
}
function safeRemoveDismissed() {
  try {
    window.sessionStorage.removeItem(DISMISS_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * iPadOS 13+ reports itself as "MacIntel"; touch points are the giveaway.
 * Shared by InstallPrompt (the iOS-only floating banner) and InstallApp
 * (the landing/dashboard strip, which also needs to know when to fall back
 * to Share-sheet instructions instead of a native install button) — was
 * previously duplicated in InstallPrompt.jsx alone.
 */
export function isIosDevice() {
  if (typeof navigator === "undefined") return false;
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * True for desktop Safari (macOS) specifically — distinct from isIosDevice().
 * Recent Safari (17.4+) can install a PWA, but — same as iOS — only via a
 * manual "File menu -> Add to Dock" gesture with no JS API to trigger it, so
 * it needs its own instructional branch rather than collapsing into either
 * "native prompt available" or "no install path exists at all".
 */
export function isDesktopSafari() {
  if (typeof navigator === "undefined" || isIosDevice()) return false;
  // Chrome, Edge, Opera, and Firefox on iOS/Android all include "Safari" in
  // their UA string for legacy compatibility — excluding their own tokens is
  // what makes this actually mean Safari rather than "any WebKit UA".
  return /^((?!chrome|android|crios|fxios|edg|opr).)*safari/i.test(navigator.userAgent);
}

/**
 * True in any browser that implements the `beforeinstallprompt` API at
 * all — Chrome, Edge, Opera, Samsung Internet, Brave, etc. — regardless of
 * whether a prompt has actually fired yet on this page load. This is the
 * fix for a real bug: `canInstall` being false does NOT mean "this browser
 * can't install" — it's just as often "already installed" (Chrome won't
 * re-offer a prompt for something already on the device — the giveaway is
 * its own "Open in app" chip in the omnibox) or "criteria not met on this
 * exact reload yet". Only Firefox and non-Chromium engines lack the API
 * outright; conflating "not ready right now" with "not supported" is what
 * caused Chrome itself to show the Firefox-only "unsupported" message.
 *
 * Feature detection beats UA sniffing here: Chromium defines
 * `window.onbeforeinstallprompt` (as `null`) unconditionally, before any
 * event fires, so this is reliable the instant the page loads — unlike
 * `canInstall`, which only flips true once the event actually arrives.
 */
export function isChromiumInstallCapable() {
  return typeof window !== "undefined" && "onbeforeinstallprompt" in window;
}

/** Media queries a launched-from-home-screen app matches, per platform. */
const STANDALONE_QUERIES = [
  "(display-mode: standalone)",
  "(display-mode: minimal-ui)",
  "(display-mode: fullscreen)",
  "(display-mode: window-controls-overlay)",
];

/** True when we're already running as an installed app rather than in a tab. */
function detectInstalled() {
  if (typeof window === "undefined") return false;
  const matches = (q) => {
    try {
      return window.matchMedia(q).matches;
    } catch {
      return false; // older browsers reject unknown display-mode queries
    }
  };
  return Boolean(
    STANDALONE_QUERIES.some(matches) ||
      // iOS Safari never supported display-mode; this is its only signal.
      window.navigator.standalone === true ||
      // Android trusted web activity wrapper.
      (document.referrer || "").startsWith("android-app://")
  );
}

/**
 * @returns {{
 *   canInstall: boolean,   // a native prompt is available right now
 *   isInstalled: boolean,  // already running as an installed app
 *   isDismissed: boolean,  // user closed an install surface previously
 *   install: () => Promise<'accepted'|'dismissed'|'unavailable'>,
 *   dismiss: () => void,
 * }}
 */
export default function usePWAInstall() {
  const [hasPrompt, setHasPrompt] = useState(() => Boolean(deferredPrompt));
  const [isInstalled, setIsInstalled] = useState(detectInstalled);
  const [isDismissed, setIsDismissed] = useState(safeReadDismissed);

  // Subscribe to the module-level slot.
  useEffect(() => {
    const sync = () => {
      setHasPrompt(Boolean(deferredPrompt));
      setIsInstalled(detectInstalled());
      setIsDismissed(safeReadDismissed());
    };
    subscribers.add(sync);
    sync(); // catch anything that landed between render and effect
    return () => subscribers.delete(sync);
  }, []);

  // display-mode flips the moment an installed copy is launched, and on some
  // platforms within the current page — so react to it rather than only
  // reading it once on mount.
  useEffect(() => {
    const mqls = STANDALONE_QUERIES.map((q) => {
      try {
        return window.matchMedia(q);
      } catch {
        return null;
      }
    }).filter(Boolean);

    const onChange = () => setIsInstalled(detectInstalled());
    mqls.forEach((mql) => {
      // Safari < 14 only has the deprecated addListener form.
      if (mql.addEventListener) mql.addEventListener("change", onChange);
      else if (mql.addListener) mql.addListener(onChange);
    });
    return () =>
      mqls.forEach((mql) => {
        if (mql.removeEventListener) mql.removeEventListener("change", onChange);
        else if (mql.removeListener) mql.removeListener(onChange);
      });
  }, []);

  /** Opens the browser's own install dialog. Resolves with the outcome. */
  const install = useCallback(async () => {
    const evt = deferredPrompt;
    if (!evt) return "unavailable";
    try {
      evt.prompt();
      const { outcome } = await evt.userChoice;
      // The event is single-use whichever way the user answered. Chrome may
      // fire a fresh one on a later navigation; until then there is genuinely
      // nothing to prompt with, so stop advertising that there is.
      deferredPrompt = null;
      if (typeof window !== "undefined") window[GLOBAL_SLOT] = null;
      notify();
      // `appinstalled` (which clears the dismissal and flips isInstalled)
      // follows on acceptance — no need to duplicate that here.
      return outcome;
    } catch {
      deferredPrompt = null;
      notify();
      return "unavailable";
    }
  }, []);

  /** Hides install surfaces for good, until install/uninstall resets it. */
  const dismiss = useCallback(() => {
    safeWriteDismissed("dismissed");
    setIsDismissed(true);
    notify();
  }, []);

  return {
    canInstall: hasPrompt && !isInstalled,
    isInstalled,
    isDismissed,
    install,
    dismiss,
  };
}
